import { Track } from "@models/track";
import { LoaderResponse } from "hls.js";
import BasicPlaybackAdapter from "./audio-adapters/BasicPlaybackAdapter";
import { default as HLSAdapter, default as HlsAdapter } from "./audio-adapters/HlsAdapter";
import IAudioAdapter from "./audio-adapters/IAudioAdapter";
import logger from "../logger";

export enum STATUS {
	UNDEFINED = "UNDEFINED",
	PAUSED = "PAUSED",
	PLAYING = "PLAYING",
	LOADED = "LOADED",
	READY = "READY",
}

enum ADAPTER {
	BasicPlaybackAdapter = "BasicPlaybackAdapter",
	HlsAdapter = "HlsAdapter",
}

type StateListener = (status: STATUS) => void;
type OnTrackEndedHandler = (track: Track) => void;

interface PlayerConstructor {
	onError?: (error: LoaderResponse) => void;
}

class Player {
	private _mediaElement: HTMLAudioElement;
	private _audioContext: AudioContext;
	private _audioAdapters: Record<string, IAudioAdapter>;

	private _status: STATUS;
	private _track?: Track;

	private _stateListeners: StateListener[];
	private _onError?: (error: LoaderResponse) => void;

	private _onTrackEndedHandler?: OnTrackEndedHandler;

	constructor({ onError = () => {} }: PlayerConstructor = {}) {
		this._mediaElement = document.createElement("audio");
		this._onError = onError;

		(this._mediaElement as any).preservesPitch = false;
		(this._mediaElement as any).webkitPreservesPitch = false;
		(this._mediaElement as any).mozPreservesPitch = false;

		window.AudioContext = window.AudioContext || (window as any).webkitAudioContext;
		this._audioContext = new AudioContext();
		this._audioContext.onstatechange = () => {
			logger.warn("AudioContext state changed: ", this._audioContext.state);
		};

		this._audioAdapters = {};
		this.initializeAudioAdapters();

		this._status = STATUS.READY;
		this._stateListeners = [];

		this._mediaElement.onended = () => {
			if (this._onTrackEndedHandler && this._track) {
				this._onTrackEndedHandler(this._track);
			}
		};
	}

	public initializeAudioAdapters(): void {
		this._audioAdapters[ADAPTER.BasicPlaybackAdapter] = new BasicPlaybackAdapter(
			this._mediaElement,
			this._audioContext,
		);

		this._audioAdapters[ADAPTER.HlsAdapter] = new HlsAdapter(this._mediaElement, this._audioContext, this._onError);
	}

	public get playback(): IAudioAdapter | undefined {
		if (this._track) {
			if (this._track.stream_url) {
				return this._audioAdapters[ADAPTER.HlsAdapter];
			} else if (this._track.sample_url) {
				return this._audioAdapters[ADAPTER.BasicPlaybackAdapter];
			}
		}

		return undefined;
	}

	public getTrack(): Track | undefined {
		return this._track;
	}

	public playbackPause(): void {
		this._audioAdapters[ADAPTER.BasicPlaybackAdapter].pause();
		this._audioAdapters[ADAPTER.HlsAdapter].pause();
	}

	public playbackReset(): void {
		this._audioAdapters[ADAPTER.BasicPlaybackAdapter].reset();
		this._audioAdapters[ADAPTER.HlsAdapter].reset();
	}

	public playPause(): void {
		const currentTime = this.playback?.getCurrentTime() || 0;
		if (this._status === STATUS.PLAYING) this.stop();
		else this.play(currentTime);
	}

	public stop(removeTrack: boolean = false): void {
		this.playback?.pause();
		if (removeTrack) {
			this._track = undefined;
		}

		this._status = STATUS.PAUSED;
		this.onStateChanged();
	}

	public async load(track: Track): Promise<void> {
		if (this.playback instanceof HLSAdapter) {
			this.playback.destroy();
			this.initializeAudioAdapters();
		}

		this._track = track;
		this._status = STATUS.LOADED;
		this.onStateChanged();
		try {
			return this.playback?.load(track);
		} catch (e) {
			logger.error("Error loading track: ", e);
			this._status = STATUS.PAUSED;
			this.onStateChanged();
			return Promise.reject(e);
		}
	}

	public async play(time: number): Promise<unknown> {
		this._status = STATUS.PLAYING;
		this.onStateChanged();
		return this.playback?.play(time);
	}

	public setVolume(volume: number): void {
		this.playback?.setVolume(volume);
	}

	public getCurrentTime(): number {
		return this.playback?.getCurrentTime() || 0;
	}

	public setCurrentTime(time: number) {
		this.playback?.setCurrentTime(time);
	}

	public getStatus(): STATUS {
		return this._status;
	}

	public isPlaying(): boolean {
		return this._status === STATUS.PLAYING;
	}

	public addStateChangeListener(fn: StateListener): void {
		this._stateListeners.push(fn);
	}

	private onStateChanged(): void {
		this._stateListeners.forEach((listener) => {
			listener(this._status);
		});
	}

	public addOnTrackEndedHandler(handler: OnTrackEndedHandler): void {
		this._onTrackEndedHandler = handler;
	}
}

export default Player;
